import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useViewer } from "../../../state/ViewerProvider";
import { useEmbedOptions } from "../../../features/embed/embedOptionsState";
import { Icon } from "../../kit";
import { CompactCheck, CompactSelect, MiniBtn } from "../inspector/rows";
import { useStudioModelId } from "../studioModel";

/**
 * The reference's Branding panel — the viewer's own logo, in two halves that
 * live in two different systems:
 *
 *  - the embed watermark (`EmbedOptions.logo.*`), persisted per model; and
 *  - the loading-screen logo (`LoaderConfig.logoImage` / `background`), a live
 *    viewer RPC that applies to the session.
 *
 * ⚠️ Explicitly NOT the Vault's `/branding` page. That one is WORKSPACE
 * branding — workspace logo, primary colour, custom domains. Different object,
 * different scope; a link at the foot says so rather than the two quietly
 * looking like the same control.
 */
/** `title` / `pad`: the Basic Share panel shows this with its own words and
 * its 18px panel gutter; the Advanced left rail uses the defaults. */
export function BrandingPanel({ title = "Branding", pad = "p-3" }: { title?: string; pad?: string } = {}) {
  const modelId = useStudioModelId();
  const { client } = useViewer();
  const r = client.ring;
  const { opts, set, uploadLogo, loading } = useEmbedOptions(modelId, { autosave: true });
  const fileRef = useRef<HTMLInputElement>(null);
  const [loaderLogo, setLoaderLogo] = useState(false);

  const logo = opts.logo ?? {};

  return (
    <div className={`flex flex-col gap-3 ${pad}`}>
      <span className="text-[12px] font-semibold text-ed-text-2">{title}</span>

      {!modelId ? (
        <p className="text-[12px] leading-snug text-ed-text-3">
          Save this piece to your Vault to put your logo on its website viewer. The
          loading-screen logo below works right away.
        </p>
      ) : loading ? (
        <span className="flex items-center gap-1.5 text-[12px] text-ed-text-3">
          <Icon name="progress_activity" size={13} className="animate-spin" /> Loading…
        </span>
      ) : (
        <>
          <span className="text-[11px] leading-snug text-ed-text-3">
            Branding logo — 180 × 48 px, up to 720 × 192 px.
          </span>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              void uploadLogo(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="flex h-16 items-center justify-center rounded border border-dashed border-ed-border bg-white transition-colors hover:border-ed-accent"
          >
            {logo.dataUrl ? (
              <img src={logo.dataUrl} alt="Brand logo" className="max-h-12 max-w-full object-contain" />
            ) : (
              <Icon name="add" size={20} className="text-ed-text-3" />
            )}
          </button>

          <label className="flex items-center justify-between gap-2">
            <span className="text-[12px] text-ed-text-2">Show logo</span>
            <CompactCheck checked={!!logo.show} onChange={(v) => set({ logo: { ...logo, show: v } })} />
          </label>

          <span className="text-[12px] font-semibold text-ed-text-2">Logo layout</span>
          <label className="flex items-center justify-between gap-2">
            <span className="text-[12px] text-ed-text-2">Fit</span>
            <CompactSelect
              value={logo.fit ?? "contain"}
              options={[{ id: "contain", label: "Contain" }, { id: "cover", label: "Cover" }]}
              onChange={(v) => set({ logo: { ...logo, fit: v as "contain" | "cover" } })}
            />
          </label>
          <label className="flex items-center justify-between gap-2">
            <span className="text-[12px] text-ed-text-2">Position</span>
            <CompactSelect
              value={logo.position ?? "left"}
              options={[
                { id: "left", label: "Left" },
                { id: "center", label: "Center" },
                { id: "right", label: "Right" },
              ]}
              onChange={(v) => set({ logo: { ...logo, position: v as "left" | "center" | "right" } })}
            />
          </label>
        </>
      )}

      <div className="flex flex-col gap-2 border-t border-ed-border pt-2">
        <span className="text-[12px] font-semibold text-ed-text-2">Loading screen</span>
        <label className="flex items-center justify-between gap-2">
          <span className="text-[12px] text-ed-text-2">Use the logo</span>
          <CompactCheck
            checked={loaderLogo}
            onChange={(v) => {
              setLoaderLogo(v);
              void r.setLoaderConfig({ logoImage: v ? (logo.dataUrl ?? "") : "" });
            }}
          />
        </label>
        <MiniBtn icon="hide_image" onClick={() => void r.setLoaderConfig({ background: "" })}>
          Remove loading background
        </MiniBtn>
      </div>

      <p className="flex items-start gap-1.5 border-t border-ed-border pt-2 text-[11px] leading-snug text-ed-text-3">
        <Icon name="info" size={13} className="mt-px shrink-0" />
        <span>
          This is the viewer’s branding for this model.{" "}
          <Link to="/branding" className="underline hover:text-ed-text">Workspace branding</Link>{" "}
          — your logo, colours and domains — is set separately.
        </span>
      </p>
    </div>
  );
}
